import { PreviewMessageType, type WebContainer } from '@webcontainer/api';
import { atom } from 'nanostores';
import { cleanStackTrace } from '~/lib/utils/stackTraceUtils';
import { chatStore } from '~/lib/stores/chat';

export interface PreviewInfo {
  port: number;
  ready: boolean;
  baseUrl: string;
}

export interface PreviewError {
  type: PreviewMessageType;
  message: string;
  stack?: string;
}

export class PreviewsStore {
  #availablePreviews = new Map<number, PreviewInfo>();
  #webcontainer: Promise<WebContainer>;

  previews = atom<PreviewInfo[]>([]);
  previewError = atom<PreviewError[]>([]);

  constructor(webcontainerPromise: Promise<WebContainer>) {
    this.#webcontainer = webcontainerPromise;

    this.#init();
  }

  async #init() {
    const webcontainer = await this.#webcontainer;

    webcontainer.on('port', (port, type, url) => {
      console.log('webcontainer port', port, type, url);

      let previewInfo = this.#availablePreviews.get(port);

      if (type === 'close' && previewInfo) {
        this.#availablePreviews.delete(port);
        this.previews.set(this.previews.get().filter((preview) => preview.port !== port));

        return;
      }

      const previews = this.previews.get();

      if (!previewInfo) {
        previewInfo = { port, ready: type === 'open', baseUrl: url };
        this.#availablePreviews.set(port, previewInfo);
        previews.push(previewInfo);
      }

      previewInfo.ready = type === 'open';
      previewInfo.baseUrl = url;

      this.previews.set([...previews]);
    });

    webcontainer.on('server-ready', (port, url) => {
      console.log('webcontainer server-ready', port, url);
    });

    webcontainer.on('error', (error) => {
      console.error('webcontainer error', error);
    });

    webcontainer.on('preview-message', (message) => {
      const previewError = this.previewError.get();
      const isAIStreaming = chatStore.get().isStreaming;

      console.log('isStreaming', isAIStreaming);

      if (isAIStreaming) {
        console.info('PreviewsStore: AI is streaming, ignoring preview message:', message);
        return; // Don't process or store the error if AI is streaming
      }

      if (message.type === 'PREVIEW_UNCAUGHT_EXCEPTION') {
        this.previewError.set([
          ...previewError,
          {
            type: message.type,
            message: message.message,
            stack: cleanStackTrace(message.stack),
          },
        ]);
      }

      if (message.type === 'PREVIEW_UNHANDLED_REJECTION') {
        this.previewError.set([
          ...previewError,
          {
            type: message.type,
            message: message.message,
            stack: cleanStackTrace(message.stack),
          },
        ]);
      }

      if (message.type === 'PREVIEW_CONSOLE_ERROR') {
        const argsAsString = message.args
          .map((arg) => {
            try {
              return typeof arg === 'string' ? arg : JSON.stringify(arg, null, 2);
            } catch {
              return String(arg);
            }
          })
          .join('\n');

        console.log('argsAsString', argsAsString);

        this.previewError.set([
          ...previewError,
          {
            type: message.type,
            message: 'Log Details',
            stack: cleanStackTrace(message.stack || '') + '\n\nConsole Args:\n' + argsAsString,
          },
        ]);
      }
    });
  }

  clearError() {
    this.previewError.set([]);
  }
}
